import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import useSignOut from 'react-auth-kit/hooks/useSignOut';
import { jwtDecode } from 'jwt-decode';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { faHourglass, faLock, faKey, faCamera, faMagnifyingGlass, faSpinner, faFileInvoice, faUsersLine, faBan, faBook, faFloppyDisk, faCashRegister } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useApiFetch from '../ApiFetch';

export default function SessionTimer({ onDisplayChange, hideClock = false }) {
    const authHeader = useAuthHeader();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const signIn = useSignIn();
    const signOut = useSignOut();
    const [secondsLeft, setSecondsLeft] = useState(0);
    const apiFetch = useApiFetch();

    const [modalExpiring, setModalExpiring] = useState(false);
    const [modalExtend, setModalExtend] = useState(false);

    const [dismissed, setDismissed] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [signingIn, setSigningIn] = useState(false);
    const [loginError, setLoginError] = useState(null);

    const modalExpiringToggle = () => {
        setModalExpiring(!modalExpiring);
        //    setResetPassEmail('');
        //    setResetting(false);
        //    setEmailSent(false);
    };
    const modalExtendToggle = () => {
        setModalExtend(!modalExtend);
    };
    const loginHandler = async (e) => {
        e.preventDefault();
        setSigningIn(true);
        setLoginError(null);
        await apiFetch.post('api/auth/login', {
            email: email,
            password: password,
        })
            .then((resp) => {
                if (resp.data) {
                    if (signIn({
                        auth: {
                            token: resp.data.token,
                            type: 'Bearer'
                        },
                        userState: { profile: resp.data.profile }
                    })) {
                        setDismissed(false);
                        setModalExtend(false);
                        setModalExpiring(false);
                        setPassword('');
                    }
                    else {
                        // Else, there must be some error. So, throw an error
                        setLoginError("Could not login.  Try again.");
                        setTimeout(() => { setLoginError(null); }, 5000);
                        setPassword('');
                    }
                }
                else {
                    setLoginError('Invalid login.');
                    setTimeout(() => { setLoginError(null); }, 5000);
                    setPassword('');
                }
            })
            .catch(ex => {
                setLoginError('Error logging in.');
                setTimeout(() => { setLoginError(null); }, 5000);
                setPassword('');
                console.error('Caught Error', ex);
            })
            .finally(() => {
                setSigningIn(false);
            });

    }



    useEffect(() => {
        const raw = authHeader;
        const token = raw?.split(' ')[1];
        if (!token) return;

        const { exp, email } = jwtDecode(token);
        setEmail(email);
        const update = () => {
            const now = Math.floor(Date.now() / 1000);
            setSecondsLeft(exp - now);
        };

        update();
        const interval = setInterval(update, 1000);

        return () => clearInterval(interval);
    }, [authHeader]);

    useEffect(() => {
        // Reset dismissal if session is no longer close to expiring
        // (e.g., token refreshed / extended)
        if (secondsLeft > 120 && dismissed) {
            setDismissed(false);
        }

        // Show modal when 2 minutes or less remain
        if (secondsLeft > 0 && secondsLeft <= 120 && !dismissed) {
            setModalExpiring(true);
        }

        // Optional: if time goes back above 2 minutes, ensure modal closes
        if (secondsLeft > 120 && modalExpiring) {
            setModalExpiring(false);
        }
    }, [secondsLeft, dismissed, modalExpiring]);

    useEffect(() => {
        if (!isAuthenticated) {
            handleSignout();
        }
    }, [isAuthenticated]);

    const handleSignout = () => {
        signOut();
        navigate('/');
    };

    const closeExpiringModal = () => {
        setModalExpiring(false);
        setDismissed(true);
    };

    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    const display = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    useEffect(() => {
        onDisplayChange?.(display);
    }, [display, onDisplayChange]);

    return <>
        {!hideClock && display}
        <Modal isOpen={modalExpiring} toggle={closeExpiringModal} size="md" fade={true} backdrop={"static"}>
            <ModalHeader toggle={closeExpiringModal}>
                <FontAwesomeIcon icon={faHourglass} className="me-2" />Session Time-Out
            </ModalHeader>
            <ModalBody className="">
                <div className="mb-3">
                    <h4>Your Sesssion is Expiring</h4>
                    <h4 className="fs-6 fw-normal text-muted my-3">As a security precaution, you will be logged out in <strong>{minutes}:{seconds.toString().padStart(2, '0')}</strong>.</h4>
                    <span className="text-muted">If additional time is required, please click "Extend Session".  To end session immediately, click "Log Out".</span>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={modalExtendToggle}>
                    <FontAwesomeIcon icon={faKey} className="me-2" />
                    Extend Session
                </Button>
                <Button color="danger" onClick={handleSignout}>
                    Log Out
                </Button>
            </ModalFooter>
        </Modal>

        <Modal isOpen={modalExtend} toggle={modalExtendToggle} size="sm" fade={true}>
            <ModalHeader toggle={modalExtendToggle}>
                <FontAwesomeIcon icon={faLock} className="me-2" />Extend Session
            </ModalHeader>
            <ModalBody className="">
                <div className="mb-3">
                    <form>
                        <h4>Extend your session</h4>
                        <h4 className="fs-6 fw-normal text-muted my-3">For security reasons, please enter your password to extend your session.</h4>
                        <div className="form-floating">
                            <input type="password" className="form-control" id="floatingPassword" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                            <label htmlFor="floatingPassword">Password</label>
                        </div>
                        {loginError ?
                            <div className="text-center mt-3">
                                <span className="text-danger">
                                    {loginError}
                                </span>
                            </div>
                            : null}
                        <button className="btn btn-primary w-100 py-2 mt-4" type="submit" onClick={loginHandler} disabled={!(password.length > 0)}>
                            <FontAwesomeIcon icon={signingIn ? faSpinner : faLock} spin={signingIn} className="me-2" />
                            Extend Session (<strong>{minutes}:{seconds.toString().padStart(2, '0')}</strong>)
                        </button>
                    </form>
                </div>
            </ModalBody>
        </Modal>


    </>;
}